import {
	type UseMutationResult,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import { api } from "@/lib/api";
import { useRateLimitedMutation } from "@/lib/use-rate-limited-mutation";
import type {
	ActivateSourceRequest,
	AddUserToDistrictManagerRequest,
	AssignDistrictManagerRequest,
	AssignManagerToDistrictRequest,
	CreateDistrictManagerRequest,
	CreateDistrictRequest,
	UpdateResellerProfileRequest,
} from "./types";

const CRUD_RATE_LIMIT = { limit: 10, windowMs: 60 * 1000 } as const;

export function useCreateDistrictManager() {
	const qc = useQueryClient();
	return useRateLimitedMutation(
		{
			mutationFn: async (data: CreateDistrictManagerRequest) => {
				const res = await api.post(
					"/api/reseller/create_district_manager/",
					data,
				);
				return res.data;
			},
			onSuccess: () => {
				qc.invalidateQueries({ queryKey: queryKeys.reseller.all });
			},
		},
		{ key: "reseller:create-manager", ...CRUD_RATE_LIMIT },
	);
}

export function useAddUserToDistrictManager() {
	const qc = useQueryClient();
	return useRateLimitedMutation(
		{
			mutationFn: async (data: AddUserToDistrictManagerRequest) => {
				const res = await api.post(
					"/api/reseller/add_user_to_district_manager/",
					data,
				);
				return res.data;
			},
			onSuccess: () => {
				qc.invalidateQueries({ queryKey: queryKeys.reseller.all });
			},
		},
		{ key: "reseller:add-user-to-manager", ...CRUD_RATE_LIMIT },
	);
}

export function useCreateDistrict() {
	const qc = useQueryClient();
	return useRateLimitedMutation(
		{
			mutationFn: async (data: CreateDistrictRequest) => {
				const res = await api.post("/api/reseller/create_district/", data);
				return res.data;
			},
			onSuccess: () => {
				qc.invalidateQueries({ queryKey: queryKeys.reseller.all });
			},
		},
		{ key: "reseller:create-district", ...CRUD_RATE_LIMIT },
	);
}

export function useAssignManagerToDistrict(): UseMutationResult<
	unknown,
	Error,
	AssignManagerToDistrictRequest
> {
	return useMutation({
		mutationFn: async (data: AssignManagerToDistrictRequest) => {
			const res = await api.post(
				"/api/reseller/assign_manager_to_district/",
				data,
			);
			return res.data;
		},
	});
}

export function useActivateSource(): UseMutationResult<
	unknown,
	Error,
	ActivateSourceRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: ActivateSourceRequest) => {
			const res = await api.post("/api/sources/activate/", data);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.districts.all });
			qc.invalidateQueries({ queryKey: queryKeys.reseller.all });
		},
	});
}

export function useAssignSourceToDistrictManager(
	sn: string,
): UseMutationResult<unknown, Error, AssignDistrictManagerRequest> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: AssignDistrictManagerRequest) => {
			const res = await api.post(
				`/api/reseller/${sn}/assign_district_manager/`,
				data,
			);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.reseller.all });
		},
	});
}

export function useUpdateResellerProfile(): UseMutationResult<
	unknown,
	Error,
	UpdateResellerProfileRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: UpdateResellerProfileRequest) => {
			const res = await api.patch("/api/reseller/my_profile/", data);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.reseller.myProfile() });
		},
	});
}

export function useDeleteDistrictManager(): UseMutationResult<
	void,
	Error,
	number
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (id: number) => {
			await api.delete(`/api/reseller/${id}/delete_district_manager/`);
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.reseller.all });
		},
	});
}
